/**
 * 双表 Chess Clock（Optical 重构）：
 * - 75% 光学透镜计时舱 + 135° 镜面高光，不再用彩色实线描边；
 * - 激活侧挂载真实 canvas Matrix Orb（加速自转 + 体积光），静默侧坍缩降暗；
 * - 大号金属冷白等宽数字；≤15 秒触发琥珀呼吸预警；PREP 显示静默读卡。
 */
import type { MatchPhase, Side, StageDefinition } from '@zongheng/core-schema';
import { sideLabel } from '@zongheng/core-schema';
import type { StagePhase } from '../../machines/contract.ts';
import { Badge, Button, cx, KeyHint } from '../ui/primitives.tsx';
import { MatrixOrb } from '../system/MatrixOrb.tsx';
import { CornerReticles, EdgeRuler } from '../system/Reticles.tsx';
import { formatClock, isClockWarning } from '../common/format.ts';

export interface ChessClockProps {
  stage: StageDefinition | null;
  remainingMs: Record<Side, number>;
  runningSide: Side | null;
  stagePhase: StagePhase;
  prepRemainingMs: number;
  phase: MatchPhase;
  selfSide: Side;
  onToggleSpeaker: () => void;
  canToggle: boolean;
}

export function ChessClock({
  stage,
  remainingMs,
  runningSide,
  stagePhase,
  prepRemainingMs,
  phase,
  selfSide,
  onToggleSpeaker,
  canToggle,
}: ChessClockProps) {
  const isPrep = stagePhase === 'PREP' && phase === 'RUNNING';
  const clockMode = stage?.clockMode ?? 'SINGLE';
  return (
    <section
      data-testid="hud-clock"
      aria-label="双表棋钟"
      className={cx('lens relative overflow-hidden p-2.5', isPrep && 'scanline')}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_112px_minmax(0,1fr)] items-stretch gap-2">
        <ClockFace
          side="AFF"
          remainingMs={remainingMs.AFF}
          running={runningSide === 'AFF' && phase === 'RUNNING'}
          self={selfSide === 'AFF'}
          muted={clockMode === 'SINGLE' && remainingMs.AFF === 0}
          stagePhase={stagePhase}
        />
        <div className="relative flex flex-col items-center justify-center gap-2 py-2">
          <span className="micro-label">{clockMode === 'CHESS' ? 'CHESS CLOCK' : 'SINGLE'}</span>
          {isPrep ? (
            <div data-testid="hud-prep" className="text-center">
              <div className="micro-label text-[#C7D2FE]">静默读卡</div>
              <div className="mono mt-1 text-3xl font-semibold text-[#E0E7FF]">
                {formatClock(prepRemainingMs)}
              </div>
              <div className="mt-1 max-w-[92px] text-[9px] leading-tight text-slate-500">倒计时结束自动开表</div>
            </div>
          ) : (
            <div className="text-center">
              <div className="micro-label">{phase === 'RUNNING' ? 'ENGAGED' : phase === 'PAUSED' ? 'PAUSED' : 'STANDBY'}</div>
              <div className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] text-slate-400">
                <span className={cx('status-dot', runningSide ? 'status-dot--live' : '')} />
                {runningSide ? `${sideLabel(runningSide)}计时` : '双方待机'}
              </div>
              <Button
                size="sm"
                variant={canToggle ? 'primary' : 'ghost'}
                className="mt-2 w-full"
                disabled={!canToggle}
                onClick={onToggleSpeaker}
                testId="hud-toggle-speaker"
                title={canToggle ? '切换发言权（空格）' : '本环节不允许切换发言权'}
              >
                切换
                <KeyHint>Space</KeyHint>
              </Button>
            </div>
          )}
          {clockMode === 'CHESS' && <Badge tone="blind">双表</Badge>}
        </div>
        <ClockFace
          side="NEG"
          remainingMs={remainingMs.NEG}
          running={runningSide === 'NEG' && phase === 'RUNNING'}
          self={selfSide === 'NEG'}
          muted={clockMode === 'SINGLE' && remainingMs.NEG === 0}
          stagePhase={stagePhase}
        />
      </div>
    </section>
  );
}

function ClockFace({
  side,
  remainingMs,
  running,
  self,
  muted,
  stagePhase,
}: {
  side: Side;
  remainingMs: number;
  running: boolean;
  self: boolean;
  muted: boolean;
  stagePhase: StagePhase;
}) {
  const low = isClockWarning(remainingMs);
  const isAff = side === 'AFF';
  const accent = isAff ? '#7DD3FC' : '#FDA4AF';
  return (
    <div
      data-testid={isAff ? 'hud-clock-aff' : 'hud-clock-neg'}
      data-running={running ? 'true' : 'false'}
      data-low={low ? 'true' : 'false'}
      data-side={side}
      aria-label={`${sideLabel(side)}剩余时间 ${formatClock(remainingMs)}`}
      className={cx(
        'relative min-h-[164px] overflow-hidden rounded-[14px] px-4 py-3 transition-all duration-500',
        running
          ? 'bg-white/[0.055] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.22),inset_0_0_0_1px_rgba(255,255,255,0.06),0_18px_46px_-24px_rgba(0,0,0,0.9)]'
          : 'bg-white/[0.018] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),inset_0_0_0_1px_rgba(255,255,255,0.025)]',
        low && 'clock-warning',
        muted && 'opacity-40',
      )}
    >
      {running && (
        <MatrixOrb
          active
          particles={680}
          accent={side}
          width={260}
          height={190}
          opacity={0.85}
          className="absolute -right-8 -top-6"
        />
      )}
      <CornerReticles inset={10} color="rgba(255,255,255,0.3)" />
      <EdgeRuler count={30} className="absolute inset-x-4 top-1.5 opacity-60" />

      <div className="relative z-[2] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{
              background: running ? accent : 'rgba(148,163,184,0.5)',
              boxShadow: running ? `0 0 10px ${accent}, 0 0 24px ${accent}55` : 'none',
            }}
          />
          <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-slate-200">
            {sideLabel(side)}
          </span>
          {self && <span className="micro-label text-slate-400">我方</span>}
        </div>
        <span className={cx('micro-label', running && 'text-[#CBD5E1]')}>
          {running ? 'LIVE' : stagePhase === 'ENDED' ? 'STOPPED' : 'STANDBY'}
        </span>
      </div>

      <div className="relative z-[2] mt-2 flex items-end justify-between">
        <div
          className={cx(
            'mono text-[58px] font-semibold leading-none tracking-[-0.035em] sm:text-[64px]',
            low ? 'text-clock-warning' : running ? 'text-timer' : 'text-timer-idle',
          )}
        >
          {formatClock(remainingMs)}
        </div>
        <div className="mb-1 text-right">
          <div className="micro-label">{running ? 'VOICE' : 'IDLE'}</div>
          <div className="mt-1 text-[10px] text-slate-500">{low ? '剩余 <15s' : running ? '发言权在此侧' : '待机'}</div>
        </div>
      </div>
    </div>
  );
}
